import type { DossierMate, DossierPlayer } from "../../hooks/useClientProfile";
import { mateKey } from "./clientCardModel";   // 27.10: ключ строки досье — без дублей null вне смены
import css from "./ClientInfoCard.module.css";

/** Блоки 2 и 3 досье клиента (20.08, Саша): «База данных» (Telegram-канал,
 *  метки аудитории) и «Unifans» (привязки приложения, юникоины, игры).
 *  Блок 1 «Юни-букинг» (история/отчёты/роster) остаётся в ClientInfoCard. */

/** Возраст по дате рождения из отчёта оператора; null — нет/мусор. */
const age = (b?: string | null) => {
  if (!b) return null;
  const d = new Date(b); if (Number.isNaN(+d)) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
  return a > 0 && a < 120 ? a : null;
};
const parseTags = (s?: string | null): string[] => { try { const v = JSON.parse(s || "[]"); return Array.isArray(v) ? v.map(String).slice(0, 8) : []; } catch { return []; } };
const VIA: Record<string, string> = { qr: "по QR", staff: "привёл оператор", manual: "вручную", waiver: "по вейверу" };

/** Метки аудитории (audience_person_meta) — красным то, что требует внимания. */
function marks(m: DossierMate): { t: string; warn: boolean }[] {
  const out: { t: string; warn: boolean }[] = [];
  if (m.tag_minor) out.push({ t: "несовершеннолетний", warn: false });
  if (m.tag_adult) out.push({ t: "18+", warn: false });
  if (m.tag_competitor) out.push({ t: "конкурент", warn: true });
  if (m.sabotage) out.push({ t: "саботаж", warn: true });
  if (m.excluded) out.push({ t: "исключён", warn: true });
  if (m.staff_mark) out.push({ t: "сотрудник", warn: false });
  if (m.blocked_at) out.push({ t: "заблокировал бота", warn: true });
  return out;
}
const personName = (m: DossierMate) => (m.name || (m.telegram_id != null ? `id${m.telegram_id}` : "Игрок")) + (m.is_owner ? " (клиент)" : "");   // вне смены id вычищен — не «idnull»

/** Блок 2 «База данных»: канал и метки по каждому связанному человеку +
 *  телеграмы из САМИХ броней клиента (привязка ботом/вручную — 25.08). */
export function DbSection({ team, bookingTgs = [] }: { team: DossierMate[]; bookingTgs?: string[] }) {
  const inCh = team.filter((m) => m.in_channel === 1 || m.joined_channel > 0).length;
  return (
    <>
      <div className={css.blockHdr}>2 · База данных <b>в канале {inCh}/{team.length}</b></div>
      {team.length === 0 && bookingTgs.length === 0 ? <div className={css.msg}>Связанных Telegram-аккаунтов в базе нет.</div> : (
        <div className={css.dsList}>
          {team.map((m, i) => (
            <div key={mateKey(m, i)} className={css.dsRow}>
              <span className={css.dsName}>{personName(m)}</span>
              {m.in_channel === 1 || m.joined_channel > 0 ? <span className={css.dsOk}>{m.in_channel === 1 ? "в канале" : "вступал в канал"}</span> : <span className={css.dsNo}>не в канале</span>}
              {marks(m).map((x) => <span key={x.t} className={x.warn ? css.dsWarn : css.dsTag}>{x.t}</span>)}
            </div>
          ))}
          {bookingTgs.map((v) => (
            <div key={v} className={css.dsRow}>
              <span className={css.dsName}>✈ {v}</span>
              <span className={css.dsTag}>указан в бронях</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Блок 3 «Unifans»: привязки приложения по номеру/связям + активность. */
export function UnifansSection({ team }: { team: DossierMate[] }) {
  const linked = team.filter((m) => m.linked);
  return (
    <>
      <div className={css.blockHdr}>3 · Unifans <b>привязано {linked.length}</b></div>
      {linked.length === 0 ? <div className={css.msg}>Ни этот номер, ни связанные с ним люди в Unifans не найдены.</div> : (
        <div className={css.dsList}>
          {linked.map((m, i) => (
            <div key={mateKey(m, i)} className={css.dsRow}>
              <span className={css.dsName}>{personName(m)}</span>
              {m.username ? <span className={css.dsTg}>@{m.username}</span> : null}
              {m.verified ? <span className={css.dsOk}>✓ подтверждён</span> : <span className={css.dsMeta}>без подтверждения</span>}
              {m.linked_via && VIA[m.linked_via] ? <span className={css.dsMeta}>{VIA[m.linked_via]}</span> : null}
              <span className={css.dsMeta}>игр {m.games_total}</span>
              {m.uc_earned > 0 ? <span className={css.dsUc}>UC {Math.round(m.uc_earned)}</span> : null}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Игроки из отчётов операторов — часть блока 1 «Юни-букинг». */
export function RosterSection({ roster }: { roster: DossierPlayer[] }) {
  if (!roster.length) return null;
  return (
    <>
      <div className={css.secLbl}>Игроки из отчётов <b>{roster.length}</b></div>
      <div className={css.dsList}>
        {roster.map((p, i) => { const a = age(p.birth_date); const tags = parseTags(p.tags); return (
          <div key={i} className={css.dsRow}>
            <span className={css.dsName}>{p.name}</span>
            {p.captain ? <span className={css.dsCap}>капитан</span> : null}
            {a != null ? <span className={css.dsMeta}>{a} лет</span> : null}
            {tags.map((t) => <span key={t} className={css.dsTag}>{t}</span>)}
            {p.times > 1 ? <span className={css.dsMeta}>×{p.times}</span> : null}
          </div>
        ); })}
      </div>
    </>
  );
}
